import React, { useEffect, useMemo } from 'react';

import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { useNetworkState } from 'react-use';

import { EDetailsMode } from 'components/Pages/Admin/Itinerary/Details/types';
import ItineraryLandingPages from 'components/Pages/ClientPortal/Itinerary/LandingPagesContainer';
import Icon from 'components/shared/Icon';
import Right from 'components/shared/Icon/icons/Right';
import Loader from 'components/shared/Loader';
import MobileAppInfo from 'components/shared/MobileAppInfo';
import Text from 'components/shared/Typography/Text';
import { ITINERARIES_ITEM_TYPES } from 'constants/index';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import i18n from 'i18n';
import { logoutThunk } from 'store/auth/thunks';
import { currentAccountSelector, userSelector } from 'store/user/selectors';
import isNoData from 'utils/isNoData';
import { getLandingTheme, LANDING_THEMES } from 'utils/itineraries/functions';
import {
  prepareProposalPages,
  prepareItineraryWithinGuestPortal,
  prepareItineraryWithinPDF,
} from 'utils/itineraries/guestPortal/prepareGuestPortal';

import PDFPreviewDataWrapper from '../../Admin/shared/PDFPreviewDataWrapper';
import { getUserName } from '../functions';
import { GuestPortalLandingPagesV2 } from '../GuestPortalLandingPagesV2';
import NotFoundWrapper from '../NotFoundWrapper';
import { GP_AUTH_SIGNIN_ROUTE, GP_ROOT_ROUTE } from '../Routes/constants';
import { useGuestPortalRouteBuilder } from '../Routes/functions';
import { getNotifications } from './functions';
import styles from './index.module.css';
import { useLoader } from './useLoader';

const ITINERARIES_ITEM_TYPE_TO_PREPARE_FN_MAP = {
  [ITINERARIES_ITEM_TYPES.GUEST_PORTAL]: prepareProposalPages,
  [ITINERARIES_ITEM_TYPES.ITINERARY]: prepareItineraryWithinGuestPortal,
};

function LandingLayout() {
  const { data: rawData, isLoading, error, itinerariesItemType, accountId, guestPortalId, refetch } = useLoader();
  const { guestPortalId: accessToken } = useParams();
  const { getGuestPortalPath, getItineraryPath, buildRoute } = useGuestPortalRouteBuilder();
  const dispatch = useDispatch();
  const user = useSelector(userSelector);
  const account = useSelector(currentAccountSelector);
  const { guestPortalHideCosts = false } = account.permissions;
  const itineraryItem = useMemo(
    () =>
      ITINERARIES_ITEM_TYPE_TO_PREPARE_FN_MAP[itinerariesItemType](rawData, {
        hideCosts: guestPortalHideCosts,
        hideEmergencyContactsIfEmpty: true,
        hideTravelWalletIfEmpty: true,
      }),
    [rawData, itinerariesItemType, guestPortalHideCosts],
  );
  const landingTheme = getLandingTheme(
    itinerariesItemType === ITINERARIES_ITEM_TYPES.ITINERARY ? itineraryItem : itineraryItem.itinerary,
  );
  const { online } = useNetworkState();

  useEffect(() => {
    if (landingTheme === LANDING_THEMES.default) {
      return;
    }

    const themeToApply = LANDING_THEMES[landingTheme];

    if (themeToApply) {
      document.body.classList.add(`landing-theme-${themeToApply}`, 'landing-guest-domain');
    }

    return () => {
      if (themeToApply) {
        document.body.classList.remove(`landing-theme-${themeToApply}`, 'landing-guest-domain');
      }
    };
  }, [landingTheme]);

  useDocumentTitle({ title: itineraryItem.title });

  if (error) {
    return <NotFoundWrapper onRetry={refetch} />;
  }

  if (isLoading) {
    return <Loader isFillBlock />;
  }

  if (itineraryItem.type !== itinerariesItemType) {
    return null;
  }

  if (itineraryItem) {
    if (localStorage.getItem('pdfPreview') === 'true') {
      return <PDFPreviewDataWrapper itemData={prepareItineraryWithinPDF(itineraryItem)} />;
    }

    const pages = itineraryItem.landing.pages;
    const isLandingV2 = itineraryItem.detailsMode === EDetailsMode.Chronological;

    return (
      <div
        className={cx(
          styles.container,
          'custom-gp-landing-layout-container',
          landingTheme !== LANDING_THEMES.default && styles.containerNomad,
          landingTheme === LANDING_THEMES.jbd && styles.containerJbd,
        )}
      >
        <div className={cx(styles.header, 'custom-gp-landing-layout-header')}>
          <Link to={buildRoute(GP_ROOT_ROUTE)} className={cx(styles.button, 'custom-gp-landing-layout-button')}>
            <Icon
              component={Right}
              width="0.7rem"
              height="1.3rem"
              className={cx(
                styles.arrow,
                styles.arrowLeft,
                'custom-gp-landing-layout-arrow',
                'custom-gp-landing-layout-arrow-left',
              )}
            />
            {i18n.t('proposal_content.guest_portal.back_to_dashboard')}
          </Link>

          {itinerariesItemType === ITINERARIES_ITEM_TYPES.GUEST_PORTAL && (
            <Link
              to={getItineraryPath(itineraryItem.itineraryId)}
              className={cx(styles.button, 'custom-gp-landing-layout-button')}
            >
              {i18n.t('proposal_content.guest_portal.switch.to_itinerary')}
              <Icon
                component={Right}
                width="0.7rem"
                height="1.3rem"
                className={cx(
                  styles.arrow,
                  styles.arrowRight,
                  'custom-gp-landing-layout-arrow',
                  'custom-gp-landing-layout-arrow-right',
                )}
              />
            </Link>
          )}
          {itinerariesItemType === ITINERARIES_ITEM_TYPES.ITINERARY && (
            <Link
              to={getGuestPortalPath(user?.currentAccount?.guestPortalId)}
              className={cx(styles.button, 'custom-gp-landing-layout-button')}
            >
              {i18n.t('proposal_content.guest_portal.switch.to_info')}
              <Icon
                component={Right}
                width="0.7rem"
                height="1.3rem"
                className={cx(
                  styles.arrow,
                  styles.arrowRight,
                  'custom-gp-landing-layout-arrow',
                  'custom-gp-landing-layout-arrow-right',
                )}
              />
            </Link>
          )}
        </div>

        {!isNoData(pages) && (
          <div className={cx(styles.landingContainer, 'custom-gp-landing-layout-landing-container')}>
            {isLandingV2 ? (
              <GuestPortalLandingPagesV2 data={itineraryItem} />
            ) : (
              <ItineraryLandingPages
                pages={pages}
                landingTheme={landingTheme}
                className="custom-main-guest-domain"
                itineraryItem={itineraryItem}
                itinerariesItemType={itinerariesItemType}
                withNavHeader
                manageHideCostsPermission={!guestPortalHideCosts}
                extraHeaderOptionsLandingPageContext={{
                  userName: getUserName(user),
                  hasUserMenu: online,
                  userMenuActions: [
                    {
                      label: i18n.t('proposal_content.log_out'),
                      onClick: () => dispatch(logoutThunk({ getSignInPath: () => buildRoute(GP_AUTH_SIGNIN_ROUTE) })),
                    },
                  ],
                  notifications: getNotifications([accountId, guestPortalId].join('_'), true),
                }}
                isPrivate
              />
            )}
          </div>
        )}

        {isNoData(pages) && (
          <div className={cx(styles.noDataContainer, 'custom-gp-landing-layout-nodata-container')}>
            <Text>No Data</Text>
          </div>
        )}
        <MobileAppInfo proposal={itineraryItem} accessToken={accessToken} />
      </div>
    );
  }
}

export default LandingLayout;
